import React from 'react';
import { MobileModal, SwipeModalRef } from './mobile-modal';
import { ModalPortal } from './modal-portal';
import { useIsSmallViewportWidth } from '@hqo/react-components-library/dist/device';
import { ModalProps } from 'components/shared/modal/modal.interface';
import { ModalContent } from './modal-content';
import { useIsDesktopHost } from 'hooks/use-host-info.hook';

const maxWidthVariants: Record<NonNullable<ModalProps['variant']>, number> = {
  small: 424,
  normal: 612,
  wide: 984,
};

export const Modal = React.forwardRef<SwipeModalRef, ModalProps>(
  (
    { children, onClose: handleClose, hideMobileCloseButton, variant = 'normal', withExitButton = true, marginTop },
    ref,
  ) => {
    const isSmallViewportWidth = useIsSmallViewportWidth();
    const isDesktop = useIsDesktopHost();
    // Layout still follows the viewport — a narrow window wants the swipe modal either way. The dismiss
    // affordance follows the HOST: inside the native shell its header already offers one, and a second X
    // directly beneath it is the duplicate this exists to remove. On desktop the caller still decides.
    //
    // Both branches, not just the narrow one: a tablet in the native app — or a phone turned landscape,
    // which is how miniapp-content ran into this in VIS-1542 — crosses the breakpoint into this layout
    // while still being a native host with a header of its own.
    const hideInModalClose = hideMobileCloseButton || !isDesktop;
    const showInModalExit = withExitButton && isDesktop;

    return (
      <ModalPortal>
        {isSmallViewportWidth ? (
          <MobileModal ref={ref} onClose={handleClose} hideMobileCloseButton={hideInModalClose}>
            {children}
          </MobileModal>
        ) : (
          <ModalContent
            maxWidth={maxWidthVariants[variant]}
            onClose={handleClose}
            variant={variant}
            withExitButton={showInModalExit}
            marginTop={marginTop}
          >
            {children}
          </ModalContent>
        )}
      </ModalPortal>
    );
  },
);

Modal.displayName = 'Modal';
